import { get } from 'lodash-es'

import type { SomeOtherString } from '../types.ts'

type Value = string | number | boolean | undefined | null | object | unknown[]

export type SortTransform = ((value: any) => Value) | Record<string, Value>
export type SortCustomTransform<T = any> = (item: T, key: keyof T | SomeOtherString) => Value

interface Parameters<T> {
  array: T[]
  key: keyof T | SomeOtherString
  order?: 'ascending' | 'asc' | 'desc' | 'descending' | ''
  transform?: SortTransform
  customTransform?: SortCustomTransform<T>
}

/**
 * @description Sort an array of objects by a certain key of the objects.
 * @param params The params to use for sorting.
 * @param params.array The array that will be sorted.
 * @param params.key The key that will be sorted on.
 * @param params.order The sorting order.
 * @param params.transform Change the values that are compared before comparison.
 * @param params.customTransform Change the values that are compared before comparison using the whole object and key as parameters in the transform function.
 */
export function sortArrayByObjectKey<T>({ array, key, order = 'ascending', transform = undefined, customTransform = undefined }: Parameters<T>): T[] {
  const indices: number[] = []
  const values: Value[] = []
  const tail: T[] = []
  array.map((item, index) => {
    const value = getValue(item, key, transform, customTransform)

    if (value === undefined || value === null) {
      tail.push(item)

      return
    }

    indices.push(index)
    values[index] = value
  })

  if (order.startsWith('asc')) {
    indices.sort((firstIndex, secondIndex) => compareValues(values[firstIndex], values[secondIndex]) || firstIndex - secondIndex)
  } else {
    indices.sort((firstIndex, secondIndex) => compareValues(values[secondIndex], values[firstIndex]) || firstIndex - secondIndex)
  }

  const result = indices.map((index) => array[index]).concat(tail)

  if (result.length !== array.length) {
    result.length = array.length
  }

  return result
}

function getValue<T>(
  item: T,
  key: keyof T | SomeOtherString,
  transform?: ((value: T[keyof T]) => Value) | Record<string, Value>,
  customTransform?: (item: T, key: keyof T | SomeOtherString) => Value,
): Value {
  if (typeof customTransform === 'function') {
    return customTransform(item, key)
  }

  const value = get(item, key)

  if (typeof transform === 'function') {
    return transform(value)
  } else if (typeof transform === 'object' && transform !== null) {
    return transform[value]
  }

  return value
}

export function compareValues(a: Value, b: Value) {
  if (typeof a === 'string' && typeof b === 'string') {
    return a.localeCompare(b)
  }

  if ((a === undefined || a === null) && (b === undefined || b === null)) {
    return 0
  }

  if ((a === undefined || a === null) && b !== undefined && b !== null) {
    return 1
  }

  if ((b === undefined || b === null) && a !== undefined && a !== null) {
    return -1
  }

  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length - b.length
  }

  if (typeof a === 'object' && typeof b === 'object') {
    return Object.keys(a as object).length - Object.keys(b as object).length
  }

  if (typeof a === 'number' && typeof b === 'number') {
    return a - b
  }

  return (a as number) - (b as number)
}
